import { useMemo } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useScopedAppFlagStore } from '@frontend/scope';

type UseFeatureAccessReturn = {
  /** The user can access the feature for at least one location */
  hasAccess: boolean;

  /** The feature flag is turned on for at least one location */
  hasEnabledFlag: boolean;

  /** Locations where the user has the ACL */
  locationsWithACLAccess: string[];

  /** Locations where the feature flag is enabled */
  locationsWithEnabledFlag: string[];
};

/**
 * Determines if a user has access to a feature based on feature flag status and an ACL permission.
 *
 * @param flagName - The feature flag name to check
 * @param acl - The permission to check
 *
 * @returns An object containing:
 * - `hasAccess`: True if user has both the ACL permission and the feature flag is enabled
 * - `hasEnabledFlag`: True if the feature flag is enabled for at least one location
 * - `locationsWithACLAccess`: Array of location IDs where user has both flag and permission
 * - `locationsWithEnabledFlag`: Array of location IDs where the feature flag is enabled
 */
export const useFeatureAccess = (flagName: string, acl: Permission): UseFeatureAccessReturn => {
  const { getFeatureFlagValue, getFeatureFlagLocationMap } = useScopedAppFlagStore();

  const locationsWithEnabledFlag = useMemo(() => {
    const featureFlagLocationMap = getFeatureFlagLocationMap(flagName);
    return Object.entries(featureFlagLocationMap)
      .filter(([_, value]) => value)
      .map(([locationId]) => locationId);
  }, [getFeatureFlagLocationMap, flagName]);

  const locationsWithACLAccess = useMemo(
    () => locationsWithEnabledFlag.filter((locationId) => hasSchemaACL(locationId, acl)),
    [locationsWithEnabledFlag, acl]
  );

  const hasEnabledFlag = getFeatureFlagValue(flagName);
  const hasACLAccess = locationsWithACLAccess.length > 0;
  const hasAccess = hasACLAccess && hasEnabledFlag;

  return { hasAccess, hasEnabledFlag, locationsWithACLAccess, locationsWithEnabledFlag };
};
